import { MARKETING_FEE, MIN_MARGIN, TARGET_MARGIN, PREBID_TRADES, MANUAL_TRADES } from '../utils/prebid';

/* The pre-bid verdict + cost breakdown. Shared by the Pre-Bid Calculator page
   and the Pre-Bid tab on Add / Update Record. `p` = computePrebid(...) result. */

export const PREBID_CSS = `
.pb-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:14px;margin-bottom:18px}
.pb-field label{display:block;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#5b6b63;margin-bottom:6px}
.pb-field .pb-help{font-size:11.5px;color:#7a8a82;margin-top:5px;line-height:1.4}
.pb-seg{display:inline-flex;border:1px solid #0D3B2E;border-radius:10px;overflow:hidden}
.pb-seg button{border:none;background:#fff;color:#0D3B2E;padding:9px 16px;font-weight:700;font-size:13px;cursor:pointer}
.pb-seg button.on{background:#0D3B2E;color:#fff}
.pb-card{background:#fff;border:1px solid #E3E9E5;border-radius:14px;padding:18px}
.pb-row{display:flex;justify-content:space-between;gap:12px;padding:8px 0;border-bottom:1px solid #F0F3F1;font-size:14px}
.pb-row:last-child{border-bottom:none}
.pb-row .hint{display:block;font-size:12px;color:#7a8a82;margin-top:2px}
.pb-row b{white-space:nowrap}
.pb-total{font-size:16px;font-weight:800;border-top:2px solid #0D3B2E;margin-top:4px;padding-top:10px}
.pb-verdict{border-radius:14px;padding:18px;margin-bottom:18px;color:#fff}
.pb-verdict.ok{background:#1B5E20}
.pb-verdict.bad{background:#B71C1C}
.pb-verdict .big{font-size:28px;font-weight:800;line-height:1.1}
.pb-verdict .sub{font-size:14px;opacity:.92;margin-top:6px}
.pb-tiles{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:12px;margin-top:14px}
.pb-tile{background:rgba(255,255,255,.14);border-radius:10px;padding:10px 12px}
.pb-tile .k{font-size:11px;text-transform:uppercase;letter-spacing:.06em;opacity:.85}
.pb-tile .v{font-size:20px;font-weight:800;margin-top:2px}
.pb-tile.goal{background:rgba(240,200,66,.28);border:1px solid rgba(240,200,66,.7)}
.pb-tile .s{font-size:11px;opacity:.9;margin-top:2px}
.pb-note{font-size:12px;color:#7a8a82;margin-top:14px;line-height:1.5}
.pb-warn{background:#FFF4E5;border:1px solid #F0C27A;color:#7A4B00;border-radius:10px;padding:10px 12px;font-size:13px;margin-bottom:14px}
`;

export const money = n => '$' + (Math.round(Number(n) || 0)).toLocaleString('en-US');
// with cents for small unit prices like $7.50 a foot
export const unitMoney = n => (Number(n) < 100 && Number(n) % 1 ? '$' + Number(n).toFixed(2) : money(n));
export const pct = n => (Number.isFinite(n) ? (n * 100).toFixed(1) : '0.0') + '%';
const MIN = Math.round(MIN_MARGIN * 100);
const GOAL = Math.round(TARGET_MARGIN * 100);

export default function PrebidResult({ p, emptyText = 'Enter the squares and the sale price to check the margin.' }) {
  if (!p) return <div className="pb-card" style={{ color: '#7a8a82' }}>{emptyText}</div>;
  return (
    <>
      <div className={'pb-verdict ' + (p.ok ? 'ok' : 'bad')}>
        <div className="big">{p.ok ? '✓ Good to go' : '⚠ Red flag'}: {pct(p.margin)} margin</div>
        <div className="sub">
          {p.ok
            ? `This bid clears the ${MIN}% minimum.`
            : p.minSale
              ? `Below the ${MIN}% minimum. The price needs to be at least ${money(p.minSale)}, that's ${money(p.minSale - p.sale)} more.`
              : `Below the ${MIN}% minimum, and this job can't reach it at a ${Math.round(p.rate * 100)}% commission rate.`}
        </div>
        <div className="pb-tiles">
          <div className="pb-tile"><div className="k">Sale price</div><div className="v">{money(p.sale)}</div></div>
          <div className="pb-tile"><div className="k">Total cost</div><div className="v">{money(p.totalCost)}</div></div>
          <div className="pb-tile"><div className="k">Profit</div><div className="v">{money(p.profit)}</div></div>
          <div className="pb-tile"><div className="k">Lowest price for {MIN}%</div><div className="v">{p.minSale ? money(p.minSale) : '—'}</div></div>
          <div className="pb-tile goal"><div className="k">Price for {GOAL}%</div><div className="v">{p.targetSale ? money(p.targetSale) : '—'}</div>
            <div className="s">{p.targetOk ? `Already at ${GOAL}% or more` : p.targetSale ? `${money(p.targetSale - p.sale)} more than this bid` : `Can't reach ${GOAL}% at a ${Math.round(p.rate * 100)}% commission`}</div></div>
        </div>
      </div>

      {p.belowFloor && (
        <div className="pb-warn">
          This bid is {unitMoney(p.perSquare)} per {p.unit}, below the {unitMoney(p.floor)} commission floor, so the rep earns no commission on it.
        </div>
      )}

      {p.manual ? <ManualBreakdown p={p} /> : <>
      <div className="pb-card">
        <div className="pb-row">
          <span>Labor &amp; material<span className="hint">{p.override
            ? `Cost override: labor ${money(p.labor)} + materials ${money(p.materials)} + other ${money(p.other)}`
            : `${p.squares} ${p.squares === 1 ? p.unit : p.units} × ${unitMoney(p.costPerSq)}`}</span></span>
          <b>{money(p.laborMaterial)}</b>
        </div>
        <div className="pb-row">
          <span>Marketing fee<span className="hint">{(MARKETING_FEE * 100).toFixed(1)}% of {money(p.sale)}</span></span>
          <b>{money(p.marketing)}</b>
        </div>
        <div className="pb-row">
          <span>Rep commission<span className="hint">
            {p.commissionEntered ? 'Cost override: entered'
              : <>{Math.round(p.rate * 100)}% × ({unitMoney(p.perSquare)} − {unitMoney(p.floor)} floor) × {p.squares} {p.squares === 1 ? p.unit : p.units}, same as the Commission Calculator</>}
          </span></span>
          <b>{money(p.commission)}</b>
        </div>
        <div className="pb-row pb-total"><span>Total cost</span><b>{money(p.totalCost)}</b></div>
        <div className="pb-row">
          <span>Profit<span className="hint">{money(p.sale)} − {money(p.totalCost)}</span></span>
          <b>{money(p.profit)}</b>
        </div>
        <div className="pb-row">
          <span>Margin<span className="hint">profit ÷ sale price · minimum {MIN}%</span></span>
          <b style={{ color: p.ok ? '#1B5E20' : '#B71C1C' }}>{pct(p.margin)}</b>
        </div>
      </div>
      <div className="pb-note">
        Labor &amp; material: {PREBID_TRADES.map(t => `${t.label} ${unitMoney(t.cost)}/${t.unit}`).join(' · ')}.
        Commission floors come from the Commission Calculator.
      </div>
      </>}
    </>
  );
}

// Trade picker (dropdown), shared by the page and the form tab
export function TradeSelect({ value, onChange, allowNone, allowManual }) {
  return (
    <select className="form-input" value={value || ''} onChange={e => onChange(e.target.value)}>
      {allowNone && <option value="">None (no pre-bid)</option>}
      {allowManual ? (
        <>
          <optgroup label="Standard cost">
            {PREBID_TRADES.map(t => <option key={t.key} value={t.key}>{t.label} ({unitMoney(t.cost)}/{t.unit})</option>)}
          </optgroup>
          <optgroup label="Type the costs yourself">
            {MANUAL_TRADES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </optgroup>
        </>
      ) : PREBID_TRADES.map(t => <option key={t.key} value={t.key}>{t.label} ({unitMoney(t.cost)}/{t.unit})</option>)}
    </select>
  );
}

// Breakdown for Roofing (type costs) / Manual: every line was typed in
function ManualBreakdown({ p }) {
  const per = x => (p.squares ? ` · ${money(x / p.squares)} / sq` : '');
  const rows = [['Labor', p.labor], ['Materials', p.materials], ['Commission', p.commission], ['Other costs', p.other]].filter(r => r[1]);
  return (
    <>
      <div className="pb-card">
        {rows.map(([k, v]) => (
          <div className="pb-row" key={k}><span>{k}<span className="hint">entered{per(v)}</span></span><b>{money(v)}</b></div>
        ))}
        <div className="pb-row">
          <span>Marketing fee<span className="hint">{(MARKETING_FEE * 100).toFixed(1)}% of {money(p.sale)}</span></span>
          <b>{money(p.marketing)}</b>
        </div>
        <div className="pb-row pb-total"><span>Total cost</span><b>{money(p.totalCost)}</b></div>
        <div className="pb-row">
          <span>Profit<span className="hint">{money(p.sale)} − {money(p.totalCost)}{per(p.profit)}</span></span>
          <b>{money(p.profit)}</b>
        </div>
        <div className="pb-row">
          <span>Margin<span className="hint">profit ÷ sale price · minimum {MIN}%</span></span>
          <b style={{ color: p.ok ? '#1B5E20' : '#B71C1C' }}>{pct(p.margin)}</b>
        </div>
      </div>
      <div className="pb-note">Price for {MIN}% and {GOAL}% keep these same costs: total cost ÷ (1 − margin).</div>
    </>
  );
}
